// Components/Common/PendingApprovalsBanner.js
//
// Legacy-style status-bar alert: a small fixed pill at the bottom that appears
// only when something is awaiting the user's action ("FTO & EPO approvals are
// awaiting your review"). Click to expand a list of areas; click an area to
// jump straight to that queue. Mirrors the old WinForms status-bar behavior.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import usePendingApprovals from "./usePendingApprovals";

const PendingApprovalsBanner = () => {
    const navigate = useNavigate();
    const { summary, markSeen } = usePendingApprovals();
    const [open, setOpen] = useState(false);

    // Banner is a NEW-arrivals alert only — it disappears once you've clicked
    // (new count → 0) and reappears only when something genuinely new arrives.
    // The standing workload lives in the bell + Approvals page, not here.
    if (!summary || summary.total <= 0) return null;

    // Cap any number so a large backlog never reads as a broken count.
    const cap = (n) => (n > 99 ? "99+" : String(n));

    // Only areas with NEW items drive this alert.
    const newAreas = summary.areas.filter((a) => a.count > 0);
    const parts = newAreas.map((a) => a.label.replace(/ Approvals| Review/g, ""));
    const headline =
        parts.length <= 2
            ? parts.join(" & ")
            : `${parts.slice(0, 2).join(", ")} +${parts.length - 2} more`;

    const go = (a) => {
        setOpen(false);
        if (a?.key) markSeen(a.key, a.cursor);   // bank cursor → NEW count drops to 0 → banner hides
        if (a?.link) navigate(a.link);
    };

    return (
        <div style={{
            position: "fixed", right: 16, bottom: 16, zIndex: 1050,
            // Round M1 — never wider than the phone viewport (was a fixed 360px,
            // which overflowed 320px screens).
            maxWidth: "min(360px, calc(100vw - 32px))",
        }}>
            {open && (
                <div style={{
                    background: "var(--ssw-surface, #fff)", border: "1px solid var(--ssw-line, #e0e0e0)", borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,.15)", marginBottom: 8, overflow: "hidden",
                }}>
                    <div style={{
                        padding: "8px 12px", borderBottom: "1px solid var(--ssw-line, #eee)",
                        fontWeight: 600, fontSize: 13, color: "var(--ssw-fg-1, #495057)",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                        <span>New since you last checked</span>
                        <span style={{ cursor: "pointer", color: "var(--ssw-fg-2, #878a99)" }} onClick={() => setOpen(false)}>
                            <i className="ri-close-line" />
                        </span>
                    </div>
                    {newAreas.map((a) => (
                        <div key={a.key}
                            onClick={() => go(a)}
                            style={{
                                padding: "10px 12px", cursor: "pointer", fontSize: 14,
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                borderBottom: "1px solid var(--ssw-line, #f5f5f5)",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fa"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}>
                            <span>{a.label}</span>
                            <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                {a.count > 0 && (
                                    <span style={{
                                        background: "#f06548", color: "#fff", borderRadius: 12,
                                        padding: "1px 8px", fontSize: 12, fontWeight: 600,
                                    }}>{cap(a.count)} new</span>
                                )}
                                <span style={{
                                    background: "var(--ssw-surface-3, #eef0f5)", color: "var(--ssw-fg-1, #495057)", borderRadius: 12,
                                    padding: "1px 8px", fontSize: 12, fontWeight: 600,
                                }} title="Total still pending">{cap(a.total)}</span>
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                style={{
                    background: "var(--ssw-primary-fill, #405189)", color: "#fff", border: "none", borderRadius: 24,
                    padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(0,0,0,.2)", display: "flex", alignItems: "center", gap: 8,
                }}
                title="New items awaiting your action">
                <i className="ri-notification-3-line" style={{ fontSize: 16 }} />
                {headline}: {cap(summary.total)} new awaiting review
                <span style={{
                    background: "#f06548", borderRadius: 12, padding: "1px 8px",
                    fontSize: 12, fontWeight: 700, marginLeft: 4,
                }}>{cap(summary.total)}</span>
            </button>
        </div>
    );
};

export default PendingApprovalsBanner;
